import { Button } from "@/components/ui/button";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { usePointerEventsGuard } from "@/hooks/usePointerEventsGuard";
import { useToast } from "@/hooks/use-toast";
import {
  APP_ROUTES,
  legacyLinkInOrg,
  ORG_PAGE_ROUTES,
  orgDocChainForSubpath,
  splitOrgPath,
} from "@/lib/appRoutes";
import { findReachableOrganization } from "@/lib/organizationSelection";
import { isTosViewBypassed } from "@/lib/tosBypass";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, type ReactNode, Suspense, useEffect, useLayoutEffect, useMemo, useState } from "react";
import RouteErrorBoundary, { routeResetKey } from "@/components/RouteErrorBoundary";
import { matchPath, Navigate, Route, Routes, useLocation } from "react-router-dom";
import TransitionOverlay from "./components/TransitionOverlay";
import LegacyLinkResolver from "./components/LegacyLinkResolver";
import OrganizationAccessDenied from "./components/OrganizationAccessDenied";
import Header from "./components/Header";
import Login from "./components/Login";
import Sidebar from "./components/Sidebar";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import RequestAccess from "./pages/RequestAccess";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CreateOrganization from "./pages/CreateOrganization";
import AcceptTermsOfService from "./pages/AcceptTermsOfService";
import Scouting from "./pages/Scouting";
import Settings from "./pages/Settings";
import TaskDetails from "./pages/TaskDetails";
import TaskAiTraits from "./pages/TaskAiTraits";
import Team from "./pages/Team";
import Traits from "./pages/Traits";
import TrialDetails from "./pages/TrialDetails";
import ExperimentDetails from "./pages/ExperimentDetails";
import { AdminDashboard } from "./pages/AdminDashboard";
import {
  ADMIN_IMAGE_RANKING_PATH,
  IMAGE_RANKING_CODEX_PATH,
  IMAGE_RANKING_DEMO_PATH,
  isImageRankingDemoRouteEnabled,
} from "./prototypes/imageRankingCodex/demoRoute";

const queryClient = new QueryClient();
const ImageRankingCodex = lazy(() => import("./pages/ImageRankingCodex"));
const AnnotationTask = lazy(() => import("./pages/AnnotationTask"));
const AdminAnnotations = lazy(() => import("./pages/AdminAnnotations"));
const CreateAnnotationTask = lazy(() => import("./pages/CreateAnnotationTask"));
const IS_STAGING_BUILD = import.meta.env.MODE === "staging";
const IS_IMAGE_RANKING_DEMO_ENABLED = isImageRankingDemoRouteEnabled(
  import.meta.env.MODE,
  import.meta.env.VITE_ENABLE_IMAGE_RANKING_DEMO
);
// appRoutes is the single source of route patterns: every <Route path> and
// standalone matchPath below reads from ORG_PAGE_ROUTES (org pages, under
// /orgs/:orgId) or APP_ROUTES (everything outside an org), so they can't drift apart.

// Main App Content Component
const AppContent = () => {
  const {
    loading,
    isAuthenticated,
    organizations,
    isSuperAdmin,
    allOrganizations,
    isNewAuth,
    clearNewAuth,
    needsTosAcceptance,
    tosConfigError,
    selectedOrganization,
    setSelectedOrganization,
    authError,
  } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { toast } = useToast();

  // The URL names the active organization. An org URL the user can reach selects that
  // org; until it is selected the page is pending, which is derived during render from
  // this render's own location and org, so no committed render pairs one org's URL with
  // another org's page. The selection runs in a layout effect, which re-renders before
  // paint, so the pending frame is never seen. The target is the object from the
  // memoized lists, so AuthContext's permission listener keeps a stable subscription.
  const urlOrg = splitOrgPath(location.pathname);
  const urlOrgId = urlOrg?.orgId;
  const urlOrgTarget = useMemo(
    () => findReachableOrganization(urlOrgId, { organizations, allOrganizations, isSuperAdmin }),
    [urlOrgId, organizations, allOrganizations, isSuperAdmin]
  );
  const urlOrgPending = Boolean(urlOrgTarget) && selectedOrganization?.id !== urlOrgTarget.id;

  useLayoutEffect(() => {
    if (isAuthenticated && !loading && urlOrgPending) {
      setSelectedOrganization(urlOrgTarget);
    }
  }, [isAuthenticated, loading, urlOrgPending, urlOrgTarget, setSelectedOrganization]);

  // Allow public access to /request-access route
  const isPublicRequestAccess = location.pathname === APP_ROUTES.requestAccess;

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Show welcome toast when user successfully lands on main app (not RequestAccess or CreateOrganization)
  useEffect(() => {
    const hasCompleteOrg = Boolean(selectedOrganization) && selectedOrganization.name !== "";
    const hasSuperAdminAccess = isSuperAdmin && allOrganizations && allOrganizations.length > 0;

    if (!loading && isAuthenticated && isNewAuth && (hasCompleteOrg || hasSuperAdminAccess)) {
      toast({
        title: "Success",
        description: "Welcome to Glean Ag!",
      });
      clearNewAuth();
    }
  }, [loading, isAuthenticated, isNewAuth, selectedOrganization, isSuperAdmin, allOrganizations, toast, clearNewAuth]);

  // Show overlay while checking authentication (includes waiting for provisioning)
  if (loading) {
    return (
      <TransitionOverlay
        open={true}
        title="Signing in..."
        subtitle="Checking your access..."
      />
    );
  }

  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow text-center">
          <h1 className="text-2xl font-semibold text-red-600">Sign In Error</h1>
          <p className="text-muted-foreground">{authError}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  // Show request access page for public /request-access route
  if (isPublicRequestAccess && !isAuthenticated) {
    return <RequestAccess />;
  }

  // Show login screen if not authenticated
  if (!isAuthenticated || (isAuthenticated && organizations === undefined)) {
    return <Login />;
  }

  // Shared annotation URLs resolve their authorized organization and its terms first.
  // They deliberately do not depend on whichever organization was selected earlier.
  // The route gate checks live membership/task eligibility and ToS configuration, so
  // unrelated org-onboarding prompts cannot intercept an authorized task link. It
  // selects the destination org for AcceptTermsOfService and subsequent app navigation.
  if (matchPath(APP_ROUTES.annotationTaskStandalone, location.pathname)) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouteErrorBoundary resetKey={location.pathname}>
            <Suspense
              fallback={<div className="grid min-h-screen place-items-center" role="status">Opening annotation task…</div>}>
              <Routes>
                <Route
                  path={APP_ROUTES.annotationTaskStandalone}
                  element={<AnnotationTask />}
                />
              </Routes>
            </Suspense>
          </RouteErrorBoundary>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  // Provisioning and the super-admin organization inventory can finish one
  // render before AuthContext's auto-selection effect runs. Keep every route
  // mounted behind the transition overlay during that gap; otherwise detail
  // pages interpret the temporary null organization as "not found" and redirect
  // away from a deep link.
  const organizationSelectionPending =
    !selectedOrganization &&
    (organizations.length > 0 || Boolean(isSuperAdmin && allOrganizations?.length));
  if (organizationSelectionPending) {
    return (
      <TransitionOverlay
        open={true}
        title="Signing in..."
        subtitle="Selecting your organization..."
      />
    );
  }

  const isAdminImageRankingRoute = location.pathname === ADMIN_IMAGE_RANKING_PATH;
  const canAccessImageRankingRoute = isSuperAdmin || IS_STAGING_BUILD;

  if (isAdminImageRankingRoute && !canAccessImageRankingRoute) {
    return <NotFound />;
  }

  // If user has no organizations, show request access (unless they're a super admin)
  const hasNoOrgs = organizations && organizations.length === 0;
  const superAdminHasOrgs = isSuperAdmin && allOrganizations && allOrganizations.length > 0;
  if (isAuthenticated && hasNoOrgs && !superAdminHasOrgs && !isAdminImageRankingRoute) {
    return <RequestAccess />;
  }

  // An old, org-less link to an org page. Sections and `/` (no documents to check) open
  // in the selected org; a trial/experiment/task link is resolved to the org that has it.
  // This runs before the selected org's setup and terms gates on purpose: they must not
  // intercept a link into another org. Only this allow-list is handled; /request-access,
  // /admin-dashboard, the retired /annotation-tasks/:taskId and unknown paths fall through.
  const docChain = selectedOrganization?.id ? orgDocChainForSubpath(location.pathname) : null;
  if (docChain) {
    if (docChain.length === 0) {
      return (
        <Navigate
          replace
          to={legacyLinkInOrg(selectedOrganization.id, location)}
        />
      );
    }
    return <LegacyLinkResolver docChain={docChain} />;
  }

  // An org URL the user cannot reach: say so rather than silently showing another org.
  // (A user with no organizations at all got RequestAccess above.)
  if (urlOrg && !urlOrgTarget) {
    return <OrganizationAccessDenied />;
  }

  // The app shell around `content`. Every return through it builds the same tree above
  // `content`, so the header and sidebar stay mounted when the content changes.
  const renderShell = (content: ReactNode) => (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <div className="flex h-screen bg-gray-50">
          <Sidebar
            isOpen={sidebarOpen}
            onClose={closeSidebar}
          />

          <div className="flex-1 flex flex-col overflow-hidden">
            <Header toggleSidebar={toggleSidebar} />

            {content}
          </div>
        </div>
      </TooltipProvider>
    </QueryClientProvider>
  );

  // The gates below act on the selected org, so while the URL's org is being selected
  // the shell holds an overlay in place of all of them, including the full-screen pages.
  if (urlOrgPending) {
    return renderShell(
      <TransitionOverlay
        open
        title="Switching organization..."
        subtitle={urlOrgTarget.name}
      />
    );
  }

  // The selected organization is one of the user's own and still incomplete (empty name):
  // a whitelisted user must finish its setup. A super admin browsing someone else's
  // unnamed org gets the normal app instead of that org's setup form.
  const hasIncompleteOrg =
    selectedOrganization?.name === "" && Boolean(organizations?.some((org) => org.id === selectedOrganization.id));
  if (isAuthenticated && hasIncompleteOrg) {
    // Keyed by org: values typed for one organization must not be submitted to another.
    return <CreateOrganization key={selectedOrganization.id} />;
  }

  // Fail closed: block access if ToS config failed to load (unless exempt)
  const isExemptFromTos = isSuperAdmin || selectedOrganization?.tosType === "customNoDataSharing";
  if (isAuthenticated && tosConfigError && !isExemptFromTos) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow text-center">
          <h1 className="text-2xl font-semibold text-red-600">Configuration Error</h1>
          <p className="text-muted-foreground">{tosConfigError}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  // Check if user needs to accept Terms of Service
  // Allow temporary bypass if content failed to load (session-only, per organization)
  const tosViewBypassed = Boolean(selectedOrganization?.id) && isTosViewBypassed(selectedOrganization.id);
  if (isAuthenticated && needsTosAcceptance && !tosViewBypassed) {
    // Keyed by org: a consent ticked for one organization's terms must not carry into another's.
    return (
      <AcceptTermsOfService
        key={selectedOrganization?.id ?? "none"}
        allowOrganizationSwitch
      />
    );
  }

  // W3: render the per-task AI-trait viewer full-page (outside the sidebar shell).
  // ImageRankingCodex sizes itself with 100vw/100vh, so embedding it in <main> (md:ml-64)
  // clips the toolbar/grid. Render standalone, gated by an active org membership. The dynamic
  // path has params, so match it with matchPath rather than a pathname === CONST check. The
  // inner single-route <Routes> gives TaskAiTraits a matched route so useParams() resolves the
  // trial/experiment/task params (rendering it bare would leave them undefined).
  const isTaskAiTraitsRoute = Boolean(matchPath(ORG_PAGE_ROUTES.taskAiTraits, location.pathname));
  if (isTaskAiTraitsRoute && selectedOrganization) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {/* These full-screen routes return before the main shell, so they need
              their own boundary. The failure they actually face is not a bad
              document but a REJECTED lazy import: after a deploy changes the
              hashed chunk names, an already-open tab 404s on the chunk, and React
              rethrows that as a plain Error. Suspense only claims thenables, so
              with no boundary it reaches the root and renders null — the same
              blank page, from a routine deploy. TaskAiTraits lazy-loads
              ImageRankingCodex itself, so this covers that too. */}
          <RouteErrorBoundary resetKey={routeResetKey(selectedOrganization?.id, location.pathname)}>
            <Routes>
              <Route path={ORG_PAGE_ROUTES.taskAiTraits} element={<TaskAiTraits />} />
            </Routes>
          </RouteErrorBoundary>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  if (isAdminImageRankingRoute) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {/* Outside <Suspense>, deliberately: the thrown thenable is claimed by
              the Suspense boundary nested within, and only real errors reach here,
              so the loading fallback still works. Note a rejected chunk import is
              sticky on the module-scope payload — "Try again" re-throws; "Go back"
              is the working escape. */}
          <RouteErrorBoundary resetKey={routeResetKey(selectedOrganization?.id, location.pathname)}>
            <Suspense
              fallback={(
                <TransitionOverlay
                  open={true}
                  title="Loading ranking viewer..."
                  subtitle="Preparing protected image data..."
                />
              )}
            >
              <ImageRankingCodex />
            </Suspense>
          </RouteErrorBoundary>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  // Show main app if authenticated and has orgs. <main> is keyed by the URL's org so
  // every org page remounts when the org changes (closing dialogs, resetting hooks)
  // while the header and sidebar above it stay mounted. Pages outside an org (the admin
  // dashboard) share one key, so a picker change there doesn't reset them (e.g. a
  // half-filled wizard).
  return renderShell(
    <main
      key={urlOrg?.orgId ?? "global"}
      className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 pt-4 md:ml-64">
      <div className="transition-all duration-150 px-4">
        {/* Bounds a render crash to the page: the sidebar, header and
            navigation survive, so a single bad document cannot blank the app. */}
        <RouteErrorBoundary resetKey={routeResetKey(selectedOrganization?.id, location.pathname)}>
          <Routes>
            <Route
              path={APP_ROUTES.requestAccess}
              element={<RequestAccess />}
            />
            <Route
              path={ORG_PAGE_ROUTES.home}
              element={<Index />}
            />
            <Route
              path={ORG_PAGE_ROUTES.traits}
              element={<Traits />}
            />
            <Route
              path={ORG_PAGE_ROUTES.team}
              element={<Team />}
            />
            <Route
              path={ORG_PAGE_ROUTES.settings}
              element={<Settings />}
            />
            <Route
              path={ORG_PAGE_ROUTES.scouting}
              element={<Scouting />}
            />
            <Route
              path={ORG_PAGE_ROUTES.trial}
              element={<TrialDetails />}
            />
            <Route
              path={ORG_PAGE_ROUTES.experiment}
              element={<ExperimentDetails />}
            />
            <Route
              path={ORG_PAGE_ROUTES.task}
              element={<TaskDetails />}
            />
            <Route
              path={ORG_PAGE_ROUTES.taskAiTraits}
              element={<TaskAiTraits />}
            />
            <Route
              path={APP_ROUTES.annotationTaskInShell}
              element={
                <Suspense fallback={<p>Opening annotation task…</p>}>
                  <AnnotationTask />
                </Suspense>
              }
            />
            <Route
              path={APP_ROUTES.adminDashboard}
              element={<AdminDashboard />}
            />
            <Route
              path={APP_ROUTES.adminAnnotations}
              element={
                <Suspense fallback={<p>Loading annotations…</p>}>
                  <AdminAnnotations />
                </Suspense>
              }
            />
            <Route
              path={APP_ROUTES.adminAnnotationsNew}
              element={
                <Suspense fallback={<p>Loading task creation…</p>}>
                  <CreateAnnotationTask />
                </Suspense>
              }
            />
            <Route
              path={APP_ROUTES.adminAnnotationTask}
              element={
                <Suspense fallback={<p>Loading annotation task…</p>}>
                  <AdminAnnotations />
                </Suspense>
              }
            />
            <Route
              path="*"
              element={<NotFound />}
            />
          </Routes>
        </RouteErrorBoundary>
      </div>
    </main>
  );
};

// Main App Component with Auth Provider
const App = () => {
  const location = useLocation();

  // App-wide safety net for the Radix "stranded body pointer-events" freeze:
  // stacked modal layers (a Dialog/Sheet containing a nested AlertDialog —
  // EditTraitDialog, EditExperimentDialog, EditCropDialog, EditLocationDialog,
  // PlotDetailsSheet, TaskList, TaskDetails, Index, Team) can leave
  // `pointer-events: none` stranded on <body> after a delete/confirm, freezing
  // the page until refresh. This clears it the instant no Radix layer is open.
  usePointerEventsGuard();

  if (location.pathname === "/privacy-policy") {
    return (
      <>
        <Toaster />
        <Sonner />
        <PrivacyPolicy />
      </>
    );
  }

  // Prototype cleanup: /image-ranking-demo is a temporary public staging route.
  // Remove it with src/prototypes/imageRankingCodex once the demo is retired.
  if (IS_IMAGE_RANKING_DEMO_ENABLED && location.pathname === IMAGE_RANKING_DEMO_PATH) {
    return (
      <>
        <Toaster />
        <Sonner />
        <Suspense
          fallback={(
            <TransitionOverlay
              open={true}
              title="Loading ranking demo..."
              subtitle="Preparing public image data..."
            />
          )}
        >
          <ImageRankingCodex bundleSource="hosted" />
        </Suspense>
      </>
    );
  }

  // Prototype cleanup: /image-ranking-codex is only for local prototype testing.
  if (import.meta.env.DEV && location.pathname === IMAGE_RANKING_CODEX_PATH) {
    return (
      <>
        <Toaster />
        <Sonner />
        <Suspense
          fallback={(
            <TransitionOverlay
              open={true}
              title="Loading ranking viewer..."
              subtitle="Preparing local image data..."
            />
          )}
        >
          <ImageRankingCodex />
        </Suspense>
      </>
    );
  }

  return (
    <>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </>
  );
};

export default App;
